'use client'

import { Pip } from '@/design-system'
import { useMockPhase } from '../hooks'
import { GalleryBody } from './GalleryBody'
import { AudioBody } from './AudioBody'
import { VideoBody } from './VideoBody'

const MONO: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 500 }

const MODES = [
  {
    name: 'Photos',
    title: 'Q2 selects · photos review',
    sub: '4 photos · client picking favorites',
    pipLabel: '2 PICKED',
    footerLabel: 'Approved list',
    footerValue: 'IMG_0421 · IMG_0423',
    Body: GalleryBody
  },
  {
    name: 'Audio',
    title: 'Q2 selects · audio review',
    sub: 'master-cut-v3.wav · timestamped notes',
    pipLabel: '3 NOTES',
    footerLabel: 'Latest comment',
    footerValue: '@0:42 · DROP REVERB',
    Body: AudioBody
  },
  {
    name: 'Video',
    title: 'Q2 selects · video review',
    sub: 'hero-cut-v3.mov · timestamped notes',
    pipLabel: '3 NOTES',
    footerLabel: 'Latest comment',
    footerValue: '@0:24 · COLOR WARM',
    Body: VideoBody
  }
] as const

interface ProofingMockProps {
  /** When set, the mock is scrubbed externally instead of running its own time loop. */
  phase?: number
}

export function ProofingMock({ phase: controlledPhase }: ProofingMockProps = {}) {
  // Reduced-motion fallback shows mode index 1 (Audio) — middle/representative.
  const loopPhase = useMockPhase(9000, 0.5, controlledPhase !== undefined)
  const phase = controlledPhase ?? loopPhase
  const modeIdx = phase < 0.334 ? 0 : phase < 0.667 ? 1 : 2
  const localPhase = (phase * 3) % 1
  const m = MODES[modeIdx]
  const Body = m.Body

  return (
    <div
      role="img"
      aria-label="Animation: proofing mock cycling through photos, audio and video review"
      style={{ position: 'relative' }}>
      <div
        style={{
          border: 'var(--border-w) solid var(--ink)',
          boxShadow: 'var(--shadow-xl)',
          background: 'var(--paper-3)',
          overflow: 'hidden'
        }}>
        <div
          style={{
            borderBottom: '2.5px solid var(--ink)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--paper-2)'
          }}>
          <span
            style={{
              width: 28,
              height: 28,
              background: 'var(--hot)',
              border: '2px solid var(--ink)',
              display: 'grid',
              placeItems: 'center'
            }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 14, color: 'var(--paper)' }}>
              P
            </span>
          </span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: '-0.01em',
                transition: 'all .3s'
              }}>
              {m.title}
            </div>
            <div style={{ ...MONO, fontSize: 10, opacity: 0.6, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
              {m.sub}
            </div>
          </div>
          <Pip bg="var(--acid)">{m.pipLabel}</Pip>
        </div>

        <Body phase={localPhase} />

        <div
          style={{
            background: 'var(--ink)',
            color: 'var(--paper)',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}>
          <span style={{ ...MONO, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.7 }}>
            {m.footerLabel}
          </span>
          <span style={{ flex: 1 }} />
          <span
            style={{
              ...MONO,
              fontSize: 10,
              fontWeight: 700,
              padding: '2px 6px',
              background: 'var(--acid)',
              color: 'var(--ink)'
            }}>
            {m.footerValue}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 29 }}>
        {MODES.map((_, i) => (
          <span
            key={i}
            style={{
              width: i === modeIdx ? 22 : 8,
              height: 8,
              background: i === modeIdx ? 'var(--ink)' : 'var(--paper-2)',
              border: '1.5px solid var(--ink)',
              transition: 'all .3s'
            }}
          />
        ))}
      </div>
    </div>
  )
}
